import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui';
import LockIcon from '@repo/ui/components/icons/Lock'; 
import EyeIcon from '@repo/ui/components/icons/EyeIcon';   
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Key } from 'lucide-react';
import { ResetPasswordSchema } from '../../schemas/ResetPassSchema';
import { apiPost } from '@repo/lib/axios/axios-utils';
import { endpoints } from '@/lib/axios/axios-global';
import { toast } from 'sonner';
import Cookies from 'js-cookie';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '@/assets/imgs/LogoBLUE.png';
import { Button, Input } from '@repo/ui';

export function ResetPasswordPage() {
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const form = useForm<z.infer<typeof ResetPasswordSchema>>({
        resolver: zodResolver(ResetPasswordSchema),
        defaultValues: {
            email: searchParams.get('email') || '',
            token: searchParams.get('token') || '',
            password: '',
            password_confirmation: '',
        },
    });

    useEffect(() => {
        const email = searchParams.get('email');
        const token = searchParams.get('token');
        if (email) {
            form.setValue('email', email);
        }
        if (token) {
            form.setValue('token', token);
        }
    }, [searchParams, form]);

    async function onSubmit(data: z.infer<typeof ResetPasswordSchema>) {
        try {
            const response = await apiPost({
                endpoint: endpoints.auth.resetPassword,
                body: {
                    email: data.email,
                    token: data.token,
                    password: data.password,
                    password_confirmation: data.password_confirmation,
                },
            });

            Cookies.set('accessToken', response.data.accessToken, { expires: 7 });
            toast.success(t('password_reset_success'), {
                position: 'top-right',
                duration: 3000,
            });
            navigate('/login', { replace: true });
        } catch (err: any) {
            let errorMessage = err?.response?.data?.errors?.email;
            if (!err?.response) {
                errorMessage = err?.response?.data?.message;
                toast.error(err?.response?.data?.message, {
                    position: 'top-right',
                    duration: 3000,
                });
            } else if (err?.response?.status === 400) {
                errorMessage = err?.response?.data?.errors?.email;
                toast.error(err?.response?.data?.errors?.email, {
                    position: 'top-right',
                    duration: 3000,
                });
            } else if (err?.response?.status === 401) {
                errorMessage = err?.response?.data?.errors?.email;
                toast.error(err?.response?.data?.errors?.email, {
                    position: 'top-right',
                    duration: 3000,
                });
            } else {
                errorMessage = err?.response?.data?.errors?.email;
                toast.error(err?.response?.data?.errors?.email, {
                    position: 'top-right',
                    duration: 3000,
                });
            }
        }
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-[#FFFFFF] font-cairo">
            <div className="my-10 md:mx-0 mx-4 w-full max-w-lg p-6 space-y-4 shadow-[0px_8px_13px_-3px_#00000012] rounded-2xl border border-[#F3E6FF] bg-[#F3E6FF] bg-opacity-10">
                <div className="max-w-[200px] max-h-[200px] mx-auto">
                    <img src={logo} alt="logo" className="mx-auto w-full h-full" />
                </div>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" dir="rtl">
                        {/* Email */}
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-[#082743] md:text-[18px] sm:text-[16px] text-[14px]">
                                        {t('email')}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative border border-[#E1DEE0] rounded-[8px]">
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                                <User width="16" height="16" />
                                            </span>
                                            <Input
                                                type="email"
                                                className="px-8"
                                                placeholder={t('email_placeholder')}
                                                readOnly
                                                {...field}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {/* Token */}
                        <FormField
                            control={form.control}
                            name="token"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-[#082743] md:text-[18px] sm:text-[16px] text-[14px]">
                                        {t('reset_token')}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative border border-[#E1DEE0] rounded-[8px]">
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                                <Key width="16" height="16" />
                                            </span>
                                            <Input
                                                type="text"
                                                className="px-8"
                                                placeholder={t('token_placeholder')}
                                                readOnly
                                                {...field}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {/* New Password */}
                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-[#082743] md:text-[18px] sm:text-[16px] text-[14px]">
                                        {t('new_password')}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative border border-[#E1DEE0] rounded-[8px]">
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                                <LockIcon width="16" height="16" />
                                            </span>
                                            <Input
                                                type={showNewPassword ? 'text' : 'password'}
                                                className="px-8 pr-12"
                                                placeholder={t('new_password_placeholder')}
                                                {...field}
                                            />
                                            <button
                                                type="button"
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                <EyeIcon width="16" height="16" />
                                            </button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {/* Confirm New Password */}
                        <FormField
                            control={form.control}
                            name="password_confirmation"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-[#082743] md:text-[18px] sm:text-[16px] text-[14px]">
                                        {t('confirm_new_password')}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative border border-[#E1DEE0] rounded-[8px]">
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                                <LockIcon width="16" height="16" />
                                            </span>
                                            <Input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                className="px-8 pr-12"
                                                placeholder={t('confirm_new_password_placeholder')}
                                                {...field}
                                            />
                                            <button
                                                type="button"
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                <EyeIcon width="16" height="16" />
                                            </button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {/* Password Requirements */}
                        <div className="text-[12px] text-[#625E74] lg:py-8 py-6">
                            <p className="font-medium">{t('strong_password')}</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>{t('password_min_length')}</li>
                                <li>{t('password_requirements')}</li>
                            </ul>
                        </div>
                        {/* Buttons */}
                        <div className="flex justify-between gap-2 w-full">
                            <Button
                                type="button"
                                onClick={() => navigate('/login')}
                                className="border-[#0E4272] text-[#0E4272] hover:bg-primery-blue-50 w-full rounded-[4px] bg-[#F5F4F5]"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                type="submit"
                                className="bg-[#0E4272] text-[#F5F4F5] w-full rounded-[4px] hover:bg-[#2066a7]"
                            >
                                {t('reset_your_password')}
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        </div>
    );
}
